import SchedulingVessel from "@/components/toto/SchedulingVessel";
import OpenButton from "@/components/toto/OpenButton";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Send, CheckCircle2,
  RotateCcw, XCircle, Plus, Trash2, Info,
} from "lucide-react";

/**
 * MT Scheduling — 6-month recurring pattern builder.
 *
 * Each MT has a weekly pattern (Mon–Sun). "Not scheduled" is the default.
 * Exceptions override the pattern for specific dates without touching it.
 *
 * Status flow:
 *   draft → submitted → (approved | changes_requested) → published
 * Only the primary reviewer (Katrina) or fallback (Rose/Danny) can approve.
 * Only the boutique GM (or Rainbow leadership) publishes to Active.
 */

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABEL = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
  thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};

const STATUS_STYLES = {
  draft:              { dot: "bg-stone-400",   label: "Draft" },
  ready_for_review:   { dot: "bg-amber-500",   label: "Ready for review" },
  submitted:          { dot: "bg-amber-500",   label: "Submitted to Katrina" },
  changes_requested:  { dot: "bg-rose-500",    label: "Changes requested" },
  approved:           { dot: "bg-emerald-500", label: "Approved · awaiting publish" },
  published:          { dot: "bg-emerald-500", label: "Active" },
};

function toTimeInput(v) { return v || ""; }
function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const hh = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

function nextMTCycleStart() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  // If we're before July, next cycle is Jul 1 this year; otherwise Jan 1 next year.
  return m < 7 ? `${y}-07-01` : `${y + 1}-01-01`;
}

function PeriodPicker({ boutique, onCreated, onCancel }) {
  const [start, setStart] = useState(nextMTCycleStart());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const create = async () => {
    setSaving(true); setErr("");
    try {
      const r = await api.post("/scheduling/periods", { boutique_code: boutique, mode: "MT", period_start: start });
      onCreated(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not create period.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="schedule-panel schedule-border rounded-3xl p-8 " data-testid="mt-new-period">
      <h2 className="font-serif-display schedule-ink text-2xl">Start a new MT schedule cycle</h2>
      <p className="text-sm schedule-muted mt-2">
        MT schedules run in six-month cycles: January – June, or July – December. Pick the cycle you're building.
      </p>
      <div className="mt-6 flex items-end gap-3 flex-wrap">
        <div>
          <div className="text-[10px] label-eyebrow schedule-muted mb-1">Cycle start</div>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            data-testid="mt-new-start"
            className="px-4 py-2 rounded-full schedule-border text-sm schedule-panel outline-none"
          />
        </div>
        <button
          onClick={create}
          disabled={saving}
          data-testid="mt-new-create"
          className="px-5 py-2 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40 flex items-center gap-2"
        >
          <Plus size={14} strokeWidth={1.5} /> Create draft
        </button>
        {onCancel && (
          <button onClick={onCancel} className="text-sm schedule-muted hover:schedule-ink px-3 py-2">
            Cancel
          </button>
        )}
      </div>
      {err && <div className="mt-4 text-sm text-rose-600" data-testid="mt-new-error">{err}</div>}
    </div>
  );
}

function PatternRow({ mt, pattern, onSave, canEdit }) {
  const [local, setLocal] = useState(() => {
    const base = {};
    DAYS.forEach((d) => { base[d] = pattern?.[d] || null; });
    return base;
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const base = {};
    DAYS.forEach((d) => { base[d] = pattern?.[d] || null; });
    setLocal(base);
  }, [pattern]);

  const dirty = useMemo(() => JSON.stringify(local) !== JSON.stringify(
    DAYS.reduce((acc, d) => { acc[d] = pattern?.[d] || null; return acc; }, {})
  ), [local, pattern]);

  const setDay = (d, value) => setLocal((cur) => ({ ...cur, [d]: value }));

  const toggleOff = (d) => {
    if (local[d]) setDay(d, null);
    else setDay(d, { start: "10:00", end: "17:00" });
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(mt.employee_id, local);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="border-t border-stone-100" data-testid={`pattern-row-${mt.employee_id}`}>
      <td className="px-4 py-3 align-middle">
        <div className="flex items-center gap-2 min-w-[160px]">
          <Avatar name={mt.employee_name} picture={mt.picture} size={28} />
          <div className="text-sm schedule-ink truncate">{mt.employee_name}</div>
        </div>
      </td>
      {DAYS.map((d) => {
        const tr = local[d];
        const isOff = !tr;
        return (
          <td key={d} className="px-2 py-2 align-middle text-center" data-testid={`pattern-cell-${mt.employee_id}-${d}`}>
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={() => canEdit && toggleOff(d)}
                disabled={!canEdit}
                aria-pressed={!isOff} data-testid={`pattern-toggle-${mt.employee_id}-${d}`}
                className={`text-[10px] px-2 py-0.5 rounded-full ${isOff ? "bg-stone-100 schedule-muted" : "bg-emerald-50 text-emerald-700"} ${canEdit ? "hover:opacity-80" : ""}`}
              >
                {isOff ? "Not scheduled" : "On"}
              </button>
              {!isOff && (
                <div className="flex items-center gap-1">
                  <input
                    type="time"
                    value={toTimeInput(tr.start)}
                    onChange={(e) => setDay(d, { ...tr, start: e.target.value })}
                    disabled={!canEdit}
                    data-testid={`pattern-start-${mt.employee_id}-${d}`}
                    className="w-20 px-1 py-0.5 text-[11px] rounded schedule-border outline-none"
                  />
                  <span className="text-[10px] schedule-muted">–</span>
                  <input
                    type="time"
                    value={toTimeInput(tr.end)}
                    onChange={(e) => setDay(d, { ...tr, end: e.target.value })}
                    disabled={!canEdit}
                    data-testid={`pattern-end-${mt.employee_id}-${d}`}
                    className="w-20 px-1 py-0.5 text-[11px] rounded schedule-border outline-none"
                  />
                </div>
              )}
            </div>
          </td>
        );
      })}
      <td className="px-3 py-3 align-middle">
        <button
          type="button"
          onClick={save}
          disabled={!canEdit || !dirty || saving}
          data-testid={`pattern-save-${mt.employee_id}`}
          className="text-[11px] px-3 py-1 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40"
        >
          {saving ? "…" : "Save"}
        </button>
      </td>
    </tr>
  );
}

function ExceptionsPanel({ period, team, onAdd, onRemove, canEdit }) {
  const [date, setDate] = useState("");
  const [empId, setEmpId] = useState("");
  const [override, setOverride] = useState(null); // null=off, or {start,end}
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const exceptions = period?.current_version?.mt_exceptions || [];

  const add = async () => {
    if (!date || !empId) return;
    setSaving(true);
    try {
      await onAdd({ date, employee_id: empId, override, note });
      setDate(""); setEmpId(""); setOverride(null); setNote("");
    } finally {
      setSaving(false);
    }
  };

  const empName = (id) => team.find((t) => t.employee_id === id)?.employee_name || id;

  return (
    <div className="schedule-panel schedule-border rounded-3xl p-6 " data-testid="mt-exceptions">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-serif-display schedule-ink text-xl">Exceptions</h2>
          <p className="text-xs schedule-muted mt-1">One-off overrides that don't change the underlying weekly pattern.</p>
        </div>
      </div>

      {canEdit && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-6 gap-2 items-end" data-testid="mt-exception-form">
          <div className="sm:col-span-1">
            <div className="text-[10px] label-eyebrow schedule-muted mb-1">Date</div>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="exc-date"
              className="w-full px-2 py-1.5 rounded-lg schedule-border text-xs outline-none" />
          </div>
          <div className="sm:col-span-2">
            <div className="text-[10px] label-eyebrow schedule-muted mb-1">Therapist</div>
            <select value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="exc-employee"
              className="w-full px-2 py-1.5 rounded-lg schedule-border text-xs outline-none">
              <option value="">— pick MT —</option>
              {team.map((t) => <option key={t.employee_id} value={t.employee_id}>{t.employee_name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <div className="text-[10px] label-eyebrow schedule-muted mb-1">Override</div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setOverride(null)} aria-pressed={override === null} data-testid="exc-off"
                className={`text-[11px] px-2 py-1 rounded-full ${override === null ? "bg-stone-900 text-white" : "bg-stone-100 schedule-muted"}`}>
                Off
              </button>
              <button type="button" onClick={() => setOverride({ start: "10:00", end: "17:00" })} aria-pressed={Boolean(override)} data-testid="exc-hours"
                className={`text-[11px] px-2 py-1 rounded-full ${override ? "bg-stone-900 text-white" : "bg-stone-100 schedule-muted"}`}>
                Hours
              </button>
              {override && (
                <>
                  <input type="time" value={override.start} onChange={(e) => setOverride({ ...override, start: e.target.value })}
                    className="w-20 px-1 py-0.5 text-[11px] rounded schedule-border outline-none" data-testid="exc-start" />
                  <span className="text-[10px] schedule-muted">–</span>
                  <input type="time" value={override.end} onChange={(e) => setOverride({ ...override, end: e.target.value })}
                    className="w-20 px-1 py-0.5 text-[11px] rounded schedule-border outline-none" data-testid="exc-end" />
                </>
              )}
            </div>
          </div>
          <div className="sm:col-span-1">
            <button onClick={add} disabled={!date || !empId || saving} data-testid="exc-add"
              className="w-full text-[11px] px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40">
              {saving ? "…" : "Add"}
            </button>
          </div>
        </div>
      )}

      {exceptions.length === 0 ? (
        <div className="mt-4 text-xs schedule-muted">No exceptions yet.</div>
      ) : (
        <ul className="mt-4 divide-y divide-stone-100" data-testid="mt-exception-list">
          {exceptions.map((e) => (
            <li key={e.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`mt-exception-${e.id}`}>
              <div className="schedule-muted text-xs w-28">{e.date}</div>
              <div className="flex-1 schedule-ink">{empName(e.employee_id)}</div>
              <div className="text-xs schedule-muted">
                {e.override ? `${fmtTime(e.override.start)} – ${fmtTime(e.override.end)}` : "Off"}
              </div>
              {canEdit && (
                <button onClick={() => onRemove(e.id)} data-testid={`mt-exception-remove-${e.id}`}
                  className="schedule-muted hover:text-rose-500"><Trash2 size={14} strokeWidth={1.5} /></button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SubmissionPanel({ period, onSubmit, onApprove, onRequestChanges, onPublish, onReopen }) {
  const [note, setNote] = useState("");
  const [changesNote, setChangesNote] = useState("");
  const [action, setAction] = useState(null); // "submit" | "approve" | "changes"
  const [saving, setSaving] = useState(false);

  const doSubmit = async () => {
    setSaving(true);
    try { await onSubmit(note); setNote(""); setAction(null); } finally { setSaving(false); }
  };
  const doApprove = async () => {
    setSaving(true);
    try { await onApprove(note); setNote(""); setAction(null); } finally { setSaving(false); }
  };
  const doChanges = async () => {
    if (!changesNote.trim()) return;
    setSaving(true);
    try { await onRequestChanges(changesNote); setChangesNote(""); setAction(null); } finally { setSaving(false); }
  };
  const doPublish = async () => {
    setSaving(true);
    try { await onPublish(); } finally { setSaving(false); }
  };
  const doReopen = async () => {
    setSaving(true);
    try { await onReopen(); } finally { setSaving(false); }
  };

  return (
    <div className="schedule-panel schedule-border rounded-3xl p-6  space-y-4" data-testid="mt-submission">
      <div className="flex items-baseline justify-between">
        <h2 className="font-serif-display schedule-ink text-xl">Approval trail</h2>
        <span className="text-[10px] label-eyebrow schedule-muted">Katrina primary · Rose &amp; Danny fallback</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {period.can_submit && (
          <button onClick={() => setAction(action === "submit" ? null : "submit")} data-testid="mt-submit-toggle"
            className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 flex items-center gap-2">
            <Send size={14} strokeWidth={1.5} /> Submit to Katrina
          </button>
        )}
        {period.can_approve && (
          <>
            <button onClick={() => setAction(action === "approve" ? null : "approve")} data-testid="mt-approve-toggle"
              className="text-sm px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2">
              <CheckCircle2 size={14} strokeWidth={1.5} /> Approve
            </button>
            <button onClick={() => setAction(action === "changes" ? null : "changes")} data-testid="mt-changes-toggle"
              className="text-sm px-4 py-2 rounded-full bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-2">
              <RotateCcw size={14} strokeWidth={1.5} /> Request changes
            </button>
          </>
        )}
        {period.can_publish && (
          <button onClick={doPublish} disabled={saving} data-testid="mt-publish"
            className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40 flex items-center gap-2">
            <CheckCircle2 size={14} strokeWidth={1.5} /> Publish to Active
          </button>
        )}
        {period.can_reopen && (
          <button onClick={doReopen} disabled={saving} data-testid="mt-reopen"
            className="text-sm px-4 py-2 rounded-full schedule-panel schedule-border schedule-ink hover:bg-stone-50 flex items-center gap-2">
            <XCircle size={14} strokeWidth={1.5} /> Reopen
          </button>
        )}
      </div>

      {action === "submit" && (
        <div className="rounded-2xl bg-stone-50 p-4 space-y-2" data-testid="mt-submit-form">
          <div className="text-[10px] label-eyebrow schedule-muted">Manager note (optional)</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="mt-submit-note"
            className="w-full text-sm px-3 py-2 rounded-lg schedule-border outline-none schedule-panel" placeholder="Anything Katrina should know before reviewing?" />
          <button onClick={doSubmit} disabled={saving} data-testid="mt-submit-confirm"
            className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40">
            Confirm submission
          </button>
        </div>
      )}
      {action === "approve" && (
        <div className="rounded-2xl bg-emerald-50 p-4 space-y-2" data-testid="mt-approve-form">
          <div className="text-[10px] label-eyebrow text-emerald-700">Approval note (optional)</div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="mt-approve-note"
            className="w-full text-sm px-3 py-2 rounded-lg schedule-border outline-none schedule-panel" placeholder="Anything to record with the approval?" />
          <button onClick={doApprove} disabled={saving} data-testid="mt-approve-confirm"
            className="text-sm px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
            Confirm approval
          </button>
        </div>
      )}
      {action === "changes" && (
        <div className="rounded-2xl bg-amber-50 p-4 space-y-2" data-testid="mt-changes-form">
          <div className="text-[10px] label-eyebrow text-amber-700">Changes requested — required</div>
          <textarea value={changesNote} onChange={(e) => setChangesNote(e.target.value)} rows={3} data-testid="mt-changes-note"
            className="w-full text-sm px-3 py-2 rounded-lg schedule-border outline-none schedule-panel" placeholder="What needs to change before you approve?" />
          <button onClick={doChanges} disabled={saving || !changesNote.trim()} data-testid="mt-changes-confirm"
            className="text-sm px-4 py-2 rounded-full bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40">
            Send back for revisions
          </button>
        </div>
      )}
    </div>
  );
}

function ChangeLog({ periodId }) {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    if (!periodId) return;
    api.get(`/scheduling/periods/${periodId}/change-log`)
      .then((r) => setEntries(r.data?.entries || []))
      .catch(() => setEntries([]));
  }, [periodId]);
  if (entries.length === 0) return null;
  return (
    <div className="schedule-panel schedule-border rounded-3xl p-6 " data-testid="mt-change-log">
      <h2 className="font-serif-display schedule-ink text-xl">History</h2>
      <ul className="mt-4 space-y-2.5">
        {entries.map((e) => (
          <li key={e.id} className="text-sm schedule-ink" data-testid={`mt-log-${e.id}`}>
            <div>{e.detail}</div>
            <div className="text-[11px] schedule-muted mt-0.5">
              {e.actor_name} · {new Date(e.created_at).toLocaleString()}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ScheduleMT() {
  const { user } = useAuth();
  const { periodId } = useParams();
  const navigate = useNavigate();

  const [boutique, setBoutique] = useState(user?.boutique_code || "PLT");
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const isLeadership = user && !user.boutique_code;

  useEffect(() => {
    setLoading(true);
    api.get("/scheduling/periods", { params: { boutique, mode: "MT" } })
      .then((r) => setPeriods(r.data?.periods || []))
      .catch(() => setPeriods([]))
      .finally(() => setLoading(false));
    api.get("/scheduling/team", { params: { boutique, role: "MT" } })
      .then((r) => setTeam(r.data?.team || []))
      .catch(() => setTeam([]));
  }, [boutique]);

  useEffect(() => {
    if (!periodId) { setPeriod(null); return; }
    api.get(`/scheduling/periods/${periodId}`)
      .then((r) => setPeriod(r.data))
      .catch(() => setPeriod(null));
  }, [periodId]);

  const refresh = async () => {
    if (periodId) {
      const r = await api.get(`/scheduling/periods/${periodId}`);
      setPeriod(r.data);
    }
    const rr = await api.get("/scheduling/periods", { params: { boutique, mode: "MT" } });
    setPeriods(rr.data?.periods || []);
  };

  const savePattern = async (employeeId, week) => {
    await api.put(`/scheduling/periods/${period.id}/mt-pattern`, { employee_id: employeeId, week });
    await refresh();
  };
  const addException = async (payload) => {
    await api.post(`/scheduling/periods/${period.id}/mt-exception`, payload);
    await refresh();
  };
  const removeException = async (id) => {
    await api.delete(`/scheduling/periods/${period.id}/mt-exception/${id}`);
    await refresh();
  };
  const submit = async (note) => { await api.post(`/scheduling/periods/${period.id}/submit`, { manager_note: note }); await refresh(); };
  const approve = async (notes) => { await api.post(`/scheduling/periods/${period.id}/approve`, { notes }); await refresh(); };
  const requestChanges = async (notes) => { await api.post(`/scheduling/periods/${period.id}/request-changes`, { notes }); await refresh(); };
  const publish = async () => { await api.post(`/scheduling/periods/${period.id}/publish`, {}); await refresh(); };
  const reopen = async () => { await api.post(`/scheduling/periods/${period.id}/reopen`); await refresh(); };

  const patterns = period?.current_version?.mt_patterns || {};
  const status = period?.status ? STATUS_STYLES[period.status] : null;

  return (
    <SchedulingVessel title={period?.label || "Massage Therapist schedules"} description="Six-month recurring schedules and individual exceptions." backTo={periodId ? "/scheduling/mt" : "/scheduling"} backLabel={periodId ? "MT schedules" : "Scheduling"}>
        <div className="flex items-baseline justify-between flex-wrap gap-3 mt-2">

          {isLeadership && (
            <select value={boutique} onChange={(e) => { setBoutique(e.target.value); navigate("/scheduling/mt"); }} data-testid="mt-boutique"
              className="px-4 py-2 rounded-full schedule-border text-sm schedule-panel outline-none">
              {["PLT", "FLL", "WYN", "PNC"].map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
        </div>

        {/* Period list / picker */}
        {!periodId && (
          <div className="mt-8 space-y-4">
            <div className="flex items-baseline justify-between">
              <h2 className="font-serif-display schedule-ink text-xl">Schedule cycles</h2>
              <button onClick={() => setShowNew((v) => !v)} aria-expanded={showNew} data-testid="mt-open-new"
                className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 flex items-center gap-2">
                <Plus size={14} strokeWidth={1.5} /> New cycle
              </button>
            </div>
            {showNew && (
              <PeriodPicker boutique={boutique} onCreated={(p) => { setShowNew(false); navigate(`/scheduling/mt/${p.id}`); }} onCancel={() => setShowNew(false)} />
            )}
            {loading ? (
              <div className="text-sm schedule-muted">Loading…</div>
            ) : periods.length === 0 ? (
              <div className="schedule-panel schedule-border rounded-2xl p-8 text-sm schedule-muted">
                No MT schedule cycles yet for {boutique}. Create one above.
              </div>
            ) : (
              <ul className="schedule-panel schedule-border rounded-2xl divide-y divide-stone-100" data-testid="mt-period-list">
                {periods.map((p) => {
                  const s = STATUS_STYLES[p.status];
                  return (
                    <li key={p.id} className="p-5 flex items-center gap-4" data-testid={`mt-period-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="schedule-ink font-medium">{p.label}</div>
                        <div className="text-[11px] schedule-muted mt-0.5 flex items-center gap-2">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
                          <span>{s.label}</span>
                          <span>·</span>
                          <span>{p.period_start} → {p.period_end}</span>
                        </div>
                      </div>
                      <OpenButton tone="dark" to={`/scheduling/mt/${p.id}`} data-testid={`mt-open-${p.id}`} aria-label={`Open ${p.label}`} />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Period detail */}
        {periodId && period && (
          <div className="mt-8 space-y-5">
            <div className="schedule-cycle-meta" data-testid="mt-period-header">
              <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[10px] label-eyebrow schedule-muted">{period.boutique_code} · MT cycle</div>

                  <div className="text-[11px] schedule-muted mt-1 flex items-center gap-2">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${status?.dot}`} />
                    <span>{status?.label}</span>
                    <span>·</span>
                    <span>{period.period_start} → {period.period_end}</span>
                    {period.current_version?.version_number > 1 && (
                      <>
                        <span>·</span>
                        <span>Version {period.current_version.version_number}</span>
                      </>
                    )}
                  </div>
                </div>
                {!period.can_edit && (
                  <div className="text-[11px] schedule-muted flex items-center gap-1.5 bg-stone-50 px-3 py-1.5 rounded-full">
                    <Info size={12} strokeWidth={1.5} /> Locked — reopen to edit
                  </div>
                )}
              </div>
            </div>

            {/* Weekly pattern grid */}
            <div className="schedule-panel schedule-border rounded-3xl overflow-hidden " data-testid="mt-patterns">
              <div className="px-6 py-4 border-b border-stone-100">
                <h2 className="font-serif-display schedule-ink text-xl">Weekly pattern</h2>
                <p className="text-xs schedule-muted mt-1">Recurring for the full six-month cycle. Use exceptions below for one-off overrides.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="text-left px-4 py-2 text-[11px] label-eyebrow schedule-muted">Therapist</th>
                      {DAYS.map((d) => (
                        <th key={d} className="text-center px-2 py-2 text-[11px] label-eyebrow schedule-muted">{DAY_LABEL[d].slice(0, 3)}</th>
                      ))}
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {team.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-6 text-sm schedule-muted text-center">No active MTs at {boutique}.</td></tr>
                    ) : team.map((mt) => (
                      <PatternRow
                        key={mt.employee_id}
                        mt={mt}
                        pattern={patterns[mt.employee_id]}
                        onSave={savePattern}
                        canEdit={period.can_edit}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <ExceptionsPanel period={period} team={team} onAdd={addException} onRemove={removeException} canEdit={period.can_edit} />

            <SubmissionPanel period={period} onSubmit={submit} onApprove={approve} onRequestChanges={requestChanges} onPublish={publish} onReopen={reopen} />

            <ChangeLog periodId={period.id} />
          </div>
        )}
    </SchedulingVessel>
  );
}
